import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { scenarioGroupFilesTabs, scenarioGroupRowStatuses } from "common/enums";
import {
    ScenarioGroupFileColumn,
    ScenarioGroupEditModalState,
    ScenarioGroupFileItem,
    ScenarioGroupFilesSource,
    ScenarioGroupGridItem,
    ScenarioGroupLinkedScenario,
    ScenarioGroupsGridFilter,
    ScenarioGroupsState
} from "./types";

const defaultGridFilter: ScenarioGroupsGridFilter = {
    searchString: null,
    pageNumber: 1,
    pageSize: 50,
    includeStatus: true
};

const defaultFilesModalState: ScenarioGroupsState["filesModal"] = {
    open: false,
    locked: false,
    selectedTab: scenarioGroupFilesTabs.settings,
    specification: null,
    source: null,
    group: null,
    filters: {},
};

const defaultGroupEditModalState: ScenarioGroupEditModalState = {
    isOpen: false,
    spinner: false,
    lookups: {
        somModes: [],
        somReleases: [],
    },
    group: null,
};

const initialState: ScenarioGroupsState = {
    showGridSpinner: false,
    modalHeader: "Edit group",
    selectedIds: [],
    groupEditModal: { ...defaultGroupEditModalState },
    selectedGroup: {},
    gridSource: {
        data: [],
        total: 0,
        filter: { ...defaultGridFilter },
    },
    linksModal: {
        open: false,
        locked: false,
        group: {},
        source: {
            data: [],
            total: 0,
        }
    },
    inputsModal: {
        open: false,
        locked: false,
        group: {},
        files: []
    },
    resultsModal: {
        open: false,
        locked: false,
        group: {},
        files: []
    },
    task: {},
    externalFunctions: [],
    functionModal: {
        open: false,
        locked: false,
        source: {}
    },
    filesModal: { ...defaultFilesModalState }
};

const scenarioGroupsSlice = createSlice({
    name: "scenarioGroups",
    initialState,
    reducers: {
        setGridSource(state, action: PayloadAction<{ data: ScenarioGroupGridItem[]; total: number }>) {
            state.gridSource.data = action.payload.data;
            state.gridSource.total = action.payload.total;
        },
        changeFilter(state, action: PayloadAction<Partial<ScenarioGroupsGridFilter>>) {
            state.gridSource.filter = {
                ...state.gridSource.filter,
                ...action.payload,
            };
        },
        toggleGridSpinner(state, action: PayloadAction<boolean>) {
            state.showGridSpinner = action.payload;
        },
        setModalHeader(state, action: PayloadAction<string>) {
            state.modalHeader = action.payload;
        },
        scenarioGroupEditModalOpen(state, action: PayloadAction<{ group: Partial<ScenarioGroupGridItem>; lookups: ScenarioGroupEditModalState["lookups"] }>) {
            state.groupEditModal = {
                isOpen: true,
                spinner: false,
                group: action.payload.group,
                lookups: action.payload.lookups,
            };
        },
        closeScenarioGroupEditModal(state) {
            state.groupEditModal = { ...defaultGroupEditModalState };
        },
        toggleScenarioGroupEditModalSpinner(state, action: PayloadAction<boolean>) {
            state.groupEditModal.spinner = action.payload;
        },
        setTask(state, action: PayloadAction<ScenarioGroupsState["task"]>) {
            state.task = action.payload;
        },
        changeSelected(state, action: PayloadAction<number[]>) {
            state.selectedIds = action.payload;
        },
        setSelectedGroup(state, action: PayloadAction<Partial<ScenarioGroupGridItem>>) {
            state.selectedGroup = action.payload;
        },
        setExternalFunctions(state, action: PayloadAction<ScenarioGroupsState["externalFunctions"]>) {
            state.externalFunctions = action.payload;
        },
        setFunctionModalSource(state, action: PayloadAction<Record<string, unknown>>) {
            state.functionModal.source = action.payload;
        },
        toggleFunctionModalOpen(state, action: PayloadAction<boolean>) {
            state.functionModal.open = action.payload;
        },
        setLinksModalGridSource(state, action: PayloadAction<{ data: ScenarioGroupLinkedScenario[]; total: number }>) {
            state.linksModal.source = {
                ...state.linksModal.source,
                ...action.payload,
            };
        },
        toggleLinksModalLocked(state, action: PayloadAction<boolean>) {
            state.linksModal.locked = action.payload;
        },
        toggleLinksModalOpen(state, action: PayloadAction<boolean>) {
            state.linksModal.open = action.payload;
        },
        setInputsModalGridSource(state, action: PayloadAction<ScenarioGroupFileItem[]>) {
            state.inputsModal.files = action.payload;
        },
        toggleInputsModalLocked(state, action: PayloadAction<boolean>) {
            state.inputsModal.locked = action.payload;
        },
        toggleInputsModalOpen(state, action: PayloadAction<boolean>) {
            state.inputsModal.open = action.payload;
        },
        setResultsModalGridSource(state, action: PayloadAction<ScenarioGroupFileItem[]>) {
            state.resultsModal.files = action.payload;
        },
        toggleResultsModalLocked(state, action: PayloadAction<boolean>) {
            state.resultsModal.locked = action.payload;
        },
        toggleResultsModalOpen(state, action: PayloadAction<boolean>) {
            state.resultsModal.open = action.payload;
        },
        setFilesModalSource(state, action: PayloadAction<ScenarioGroupFilesSource>) {
            state.filesModal.source = {
                ...(state.filesModal.source || {}),
                ...action.payload,
            };
        },
        resetFilesModal(state) {
            state.filesModal = { ...defaultFilesModalState };
        },
        toggleFilesModalLocked(state, action: PayloadAction<boolean>) {
            state.filesModal.locked = action.payload;
        },
        toggleFilesModalOpen(state, action: PayloadAction<boolean>) {
            state.filesModal.open = action.payload;
        },
        setFilesSpecification(state, action: PayloadAction<Record<string, ScenarioGroupFileColumn[]>>) {
            state.filesModal.specification = action.payload;
        },
        setFilesModalGroup(state, action: PayloadAction<{ groupId: number; somModeName: string } | null>) {
            state.filesModal.group = action.payload;
        },
        changeRow(state, action: PayloadAction<{ file: string; index: number; data: Record<string, unknown> }>) {
            const row = state.filesModal.source?.[action.payload.file]?.[action.payload.index];
            if (!row) {
                return;
            }

            if (row.type === scenarioGroupRowStatuses.unchanged) {
                row.type = scenarioGroupRowStatuses.changed;
            }

            row.data = {
                ...row.data,
                ...action.payload.data,
            };
        },
        deleteRow(state, action: PayloadAction<{ file: string; index: number }>) {
            const row = state.filesModal.source?.[action.payload.file]?.[action.payload.index];
            if (row) {
                row.type = scenarioGroupRowStatuses.hidden;
            }
        },
        setFilesModalFilter(state, action: PayloadAction<{ file: string; filter: Record<string, unknown> }>) {
            state.filesModal.filters[action.payload.file] = action.payload.filter;
        },
        updateFilesModalFilter(state, action: PayloadAction<{ file: string; filter: Record<string, unknown> }>) {
            state.filesModal.filters[action.payload.file] = {
                ...(state.filesModal.filters[action.payload.file] || {}),
                ...action.payload.filter,
            };
        },
        addRow(state, action: PayloadAction<{ file: string; data?: Record<string, unknown> }>) {
            const fileSource = state.filesModal.source?.[action.payload.file];
            if (!fileSource) {
                return;
            }

            fileSource.push({
                type: scenarioGroupRowStatuses.new,
                index: fileSource.length,
                data: action.payload.data || {},
                errors: {},
            });
        },
        setFilesModalErrors(state, action: PayloadAction<{ file: string; index: number; errors: Record<string, unknown> }[]>) {
            action.payload.forEach(({ file, index, errors }) => {
                const row = state.filesModal.source?.[file]?.[index];
                if (row) {
                    row.errors = {
                        ...row.errors,
                        ...errors,
                    };
                }
            });
        },
        resetFilesModalErrors(state) {
            if (!state.filesModal.source) {
                return;
            }

            Object.values(state.filesModal.source).forEach((rows) => {
                rows.forEach((row) => {
                    row.errors = {};
                });
            });
        },
        setFilesModalTab(state, action: PayloadAction<number>) {
            state.filesModal.selectedTab = action.payload;
        },
        reset() {
            return {
                ...initialState,
                gridSource: {
                    ...initialState.gridSource,
                    filter: { ...defaultGridFilter },
                },
                groupEditModal: { ...defaultGroupEditModalState },
                filesModal: { ...defaultFilesModalState },
            };
        },
    },
});

export const actions = scenarioGroupsSlice.actions;
export default scenarioGroupsSlice.reducer;
